import { useTranslation } from 'react-i18next';

import { groupsOf } from '@/api/auth';
import { useSession } from '@/app/session';
import { cn } from '@/lib/cn';
import NotFound from '@/pages/NotFound';
import { type } from '@/theme/typography';

import { BrandingSection } from './BrandingSection';
import { maySeeSettings } from './settingsAccess';
import { UsersSection } from './UsersSection';

/**
 * `DESIGN.md` §6.10 — **the last address in §5's table that had no screen** (TASK-101).
 *
 * §6.10 is four lines long and *"deliberately thin"*, which is an instruction to add nothing beyond
 * what is needed rather than permission to invent: two sections, users and branding, and no third.
 * What the four lines did not specify — the states, the copy, where each control lives, what the
 * invitation dialog says — was added to §6.10 by this task and is listed in its `Result`.
 *
 * ⚠️ **`Admin` only, and the read gate is here as well as on the route.** `RequireGroup` in
 * `routes.tsx` answers a Recruiter or an Interviewer with the same Not-found an unknown address gets
 * (§5, and never a Forbidden, which would confirm the screen exists). This second check is what stops
 * a **demotion without a re-login** — `useSession` has a `staleTime` — from leaving a cached directory
 * on screen for somebody who has just lost the right to it, and it is the same shape §6.9's screens
 * use.
 *
 * ⚠️ **One `h1` and two sections** (§9). The sections are `Users` first and `Branding` second, in
 * §6.10's own order: who works here is the part with consequences, and a colour is the part somebody
 * came for once.
 */
export default function Settings() {
  const { t } = useTranslation('settings');
  const session = useSession();

  if (session.data === undefined || !maySeeSettings(groupsOf(session.data))) {
    return <NotFound />;
  }

  return (
    <div className="mx-auto max-w-4xl">
      <header>
        <h1 className={type.pageTitle}>{t('title')}</h1>
        <p className={cn(type.body, 'mt-1 max-w-[68ch]')}>{t('lead')}</p>
      </header>

      <div className="mt-8">
        <UsersSection viewerId={session.data.user.id} />
        <BrandingSection />
      </div>
    </div>
  );
}
